/**
 * Whether a homeowner may use tasks and the assistant.
 * CommonJS only. No object spread: the iOS bundle cannot load Babel's spread helper.
 *
 * A live sponsor is entitled as sponsored. When the sponsor does not allow
 * access, the homeowner account's plan, sponsor_free_until, and grace_until
 * are read before the result is unpaid.
 */

const { subscriptionAllowsAccess } = require('../utils/subscriptionAccess');

const ENTITLEMENT_REASONS = [
  'sponsored',
  'sponsorless_paid',
  'sponsorless_free_period',
  'grace',
  'unpaid',
];

const HOMEOWNER_ASSISTANT_MESSAGE = "The assistant isn't included right now. Your home and records stay in this account.";
const HOMEOWNER_TASK_MESSAGE = "New tasks aren't included right now. Tasks you already have stay in this account.";
const HOMEOWNER_PROJECT_MESSAGE = "New projects aren't included right now. Projects you already have stay in this account.";
const HOMEOWNER_PLAN_INACTIVE_MESSAGE = "Your plan isn't active. Projects you already have stay in this account.";
const TASKS_UNAVAILABLE_MESSAGE = 'Tasks are unavailable right now. Please try again.';
const HOME_REPORT_PLAN_MESSAGE = 'Requires a Pacarion plan.';
const HOME_REPORT_AGENT_PLAN_MESSAGE = "This homeowner's plan isn't active";
const HOME_REPORT_SUBSCRIBE_LEAD = 'Subscribe to create Home History Reports, keep adding tasks and use the assistant. There is no free trial.';

function sponsorId(client) {
  if (!client || typeof client !== 'object') return '';
  const assigned = client.assigned_agent_id;
  const owner = client.agent_id;
  const id = assigned || owner || '';
  return String(id).trim();
}

function sponsorProfile(client) {
  if (!client || typeof client !== 'object') return null;
  if (client.agent && typeof client.agent === 'object') return client.agent;
  return null;
}

function atMs(now) {
  if (now instanceof Date) return now.getTime();
  return new Date(now || Date.now()).getTime();
}

function inTrialGrace(profile, now) {
  if (!profile || profile.subscription_status !== 'trialing' || !profile.trial_ends_at) return false;
  const trialMs = new Date(profile.trial_ends_at).getTime();
  if (Number.isNaN(trialMs)) return false;
  return atMs(now) > trialMs;
}

function billingRecord(client) {
  if (!client || typeof client !== 'object') return null;
  if (client.account && typeof client.account === 'object') return client.account;
  return client;
}

function inFuture(value, now) {
  if (!value) return false;
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return false;
  return ms > atMs(now);
}

function ownPlanEntitlement(client, now) {
  const billing = billingRecord(client);
  if (!billing) return null;
  const status = String(billing.plan_status || '').toLowerCase();
  const periodOpen = !billing.current_period_end || inFuture(billing.current_period_end, now);
  if ((status === 'active' || status === 'trialing') && periodOpen) {
    return { entitled: true, reason: 'sponsorless_paid' };
  }
  if (inFuture(billing.sponsor_free_until, now)) {
    return { entitled: true, reason: 'sponsorless_free_period' };
  }
  if (inFuture(billing.grace_until, now)) {
    return { entitled: true, reason: 'grace' };
  }
  return null;
}

/**
 * @param {object} client homeowner row. Pass the sponsor profile as client.agent.
 *   Pass billing columns as client.account (plan_status, current_period_end,
 *   grace_until, sponsor_free_until).
 * @returns {{ entitled: boolean, reason: 'sponsored'|'sponsorless_paid'|'sponsorless_free_period'|'grace'|'unpaid' }}
 */
function homeownerEntitlement(client, now) {
  const at = now || new Date();
  if (sponsorId(client)) {
    const profile = sponsorProfile(client);
    if (profile && subscriptionAllowsAccess(profile, at)) {
      if (profile.subscription_status === 'past_due' || inTrialGrace(profile, at)) {
        return { entitled: true, reason: 'grace' };
      }
      return { entitled: true, reason: 'sponsored' };
    }
  }
  const own = ownPlanEntitlement(client, at);
  if (own) return own;
  return { entitled: false, reason: 'unpaid' };
}

module.exports = {
  ENTITLEMENT_REASONS,
  HOMEOWNER_ASSISTANT_MESSAGE,
  HOMEOWNER_TASK_MESSAGE,
  HOMEOWNER_PROJECT_MESSAGE,
  HOMEOWNER_PLAN_INACTIVE_MESSAGE,
  TASKS_UNAVAILABLE_MESSAGE,
  HOME_REPORT_PLAN_MESSAGE,
  HOME_REPORT_AGENT_PLAN_MESSAGE,
  HOME_REPORT_SUBSCRIBE_LEAD,
  homeownerEntitlement,
};
